import { useEffect, useState } from "react";

type TypewriterRotatorProps = {
  phrases: string[];
  typeSpeed?: number;
  holdMs?: number;
  fadeMs?: number;
  className?: string;
};

export function TypewriterRotator({
  phrases,
  typeSpeed = 75,
  holdMs = 1400,
  fadeMs = 400,
  className,
}: TypewriterRotatorProps) {
  const [index, setIndex] = useState(0);
  // Start with the full first phrase so the server-rendered heading has
  // content for crawlers; the typing effect takes over after hydration.
  const [text, setText] = useState(phrases[0] ?? "");
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const phrase = phrases[index] ?? "";
    let cancelled = false;
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        const id = setTimeout(resolve, ms);
        timeouts.push(id);
      });

    (async () => {
      setVisible(true);
      setText("");
      for (let i = 1; i <= phrase.length; i++) {
        if (cancelled) return;
        setText(phrase.slice(0, i));
        await wait(typeSpeed);
      }
      if (cancelled) return;
      await wait(holdMs);
      if (cancelled) return;
      setVisible(false);
      await wait(fadeMs);
      if (cancelled) return;
      setIndex((i) => (i + 1) % phrases.length);
    })();

    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
    };
  }, [index, phrases, typeSpeed, holdMs, fadeMs]);

  return (
    <span
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transition: `opacity ${fadeMs}ms ease`,
      }}
    >
      {text}
      <span
        aria-hidden
        className="ml-1 inline-block w-[2px] -translate-y-[2px] bg-current align-middle animate-pulse"
        style={{ height: "0.9em" }}
      />
    </span>
  );
}